﻿import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import {
  Volume2,
  QrCode,
  Download,
  Home,
  Save,
  Mic,
  Loader,
  CheckCircle2,
  AlertTriangle,
  Pill,
  Smartphone,
  Printer,
  StickyNote,
  Play,
  Pause,
  RotateCcw,
  Upload
} from 'lucide-react';
import type { AnalysisResult, PrescriptionAnalysis } from '../types';
import { savePrescription } from '../services/firebaseService';
import '../styles/AudioQRCode.css';

const QR_SCHEDULE_STORAGE_PREFIX = 'medcheck_qr_schedule_';

function getQrScheduleUrl(prescriptionId: string): string {
  const origin =
    window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.')
      ? window.location.origin
      : 'https://medcheck26.web.app';
  return `${origin}/qr-schedule?id=${encodeURIComponent(prescriptionId)}`;
}

function createPrescriptionRecord(analysis: AnalysisResult, prescriptionId: string): Omit<PrescriptionAnalysis, 'createdAt'> {
  return {
    prescriptionId,
    userId: 'anonymous',
    medicines: analysis.medicines,
    interactions: analysis.interactions || [],
    issues: analysis.issues || [],
    safetyScore: analysis.safetyScore,
    warnings: analysis.warnings || [],
    imageUrl: analysis.imageUrl || '',
    extractedText: analysis.extractedText || analysis.ocrText || '',
    ocrText: analysis.ocrText,
    modelUsage: analysis.modelUsage,
    patientInfo: analysis.patientInfo,
    status: 'completed',
  };
}

export const AudioQRCode: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [checkedMeds, setCheckedMeds] = useState<Set<number>>(new Set());
  const [prescriptionUrl, setPrescriptionUrl] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  const analysis = location.state?.analysis as AnalysisResult | undefined;

  // Generate and save prescription URL when component mounts
  useEffect(() => {
    const generatePrescriptionUrl = async () => {
      if (!analysis) return;
      
      // Generate unique ID
      const prescriptionId = `rx-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const prescriptionRecord = createPrescriptionRecord(analysis, prescriptionId);
      const scheduleUrl = getQrScheduleUrl(prescriptionId);
      localStorage.setItem(`${QR_SCHEDULE_STORAGE_PREFIX}${prescriptionId}`, JSON.stringify(prescriptionRecord));
      // Keep the QR compact: it points to the saved schedule record only.
      setPrescriptionUrl(scheduleUrl);
      
      // Try to save to Firebase in background (non-blocking)
      setIsSaving(true);
      try {
        
        // Create timeout promise (10 seconds for first connection)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Firebase timeout')), 10000)
        );
        
        // Try to save with timeout
        await Promise.race([
          savePrescription(prescriptionRecord),
          timeoutPromise
        ]);

        // If save succeeded, upgrade to shareable URL
        const shareableUrl = scheduleUrl;
        setPrescriptionUrl(shareableUrl);
      } catch {
        setPrescriptionUrl(scheduleUrl);
      } finally {
        setIsSaving(false);
      }
    };

    generatePrescriptionUrl();
  }, [analysis]);

  if (!analysis) {
    return (
      <div className="audio-qr-code">
        <div className="page-bg">
          <div className="bg-gradient-1"></div>
          <div className="bg-gradient-2"></div>
        </div>
        <motion.div 
          className="error-container"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="error-icon">
            <AlertTriangle size={48} />
          </div>
          <h2>No Analysis Data</h2>
          <p>Please analyze a prescription first to generate audio instructions and QR codes</p>
          <motion.button 
            className="btn btn-primary"
            onClick={() => navigate('/upload')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Upload size={20} />
            <span>Upload Prescription</span>
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const generateAudio = async () => {
    setIsGenerating(true);
    try {
      const medicineInstructions = analysis.medicines
        .map(
          (m) =>
            `Take ${m.name} ${m.dosage} ${m.frequency} times daily after meals. Continue for ${m.duration}.`
        )
        .join(' ');

      const warnings = analysis.warnings.length
        ? `Important warnings: ${analysis.warnings.join(' ')}`
        : '';

      const fullText = `Your prescription analysis: ${medicineInstructions} ${warnings}`;

      const utterance = new SpeechSynthesisUtterance(fullText);
      utterance.rate = 0.9;
      utterance.pitch = 1;

      window.speechSynthesis.speak(utterance);
      setIsPlaying(true);

      utterance.onend = () => {
        setIsPlaying(false);
      };

      setAudioUrl(`data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAAB9AAACABAAZGF0YQIAAAAAAA==`);
    } catch (error) {
      console.error('Audio generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const stopAudio = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
  };

  const downloadQRCode = () => {
    if (qrRef.current) {
      const svg = qrRef.current.querySelector('svg');
      if (svg) {
        // Convert SVG to canvas for download
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const svgData = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          
          const link = document.createElement('a');
          link.href = canvas.toDataURL('image/png');
          link.download = `prescription-qr-${Date.now()}.png`;
          link.click();
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      }
    }
  };

  const toggleMedicine = (idx: number) => {
    const newChecked = new Set(checkedMeds);
    if (newChecked.has(idx)) {
      newChecked.delete(idx);
    } else {
      newChecked.add(idx);
    }
    setCheckedMeds(newChecked);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="audio-qr-code">
      {/* Background */}
      <div className="page-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-grid"></div>
      </div>

      <div className="container">
        {/* Page Header */}
        <motion.div 
          className="page-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="header-icon">
            <Volume2 size={28} />
          </div>
          <div className="header-text">
            <h1>Audio & QR Code</h1>
            <p>Generate voice instructions and printable QR codes for your prescription</p>
          </div>
        </motion.div>

        <motion.div 
          className="content-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Audio Section */}
          <motion.div className="section audio-section" variants={itemVariants}>
            <div className="section-header">
              <div className="section-icon">
                <Mic size={20} />
              </div>
              <div>
                <h2>Voice Instructions</h2>
                <p>Listen to audio guidance for your medicines</p>
              </div>
            </div>

            <div className="section-content">
              <div className="audio-visual">
                <div className={`audio-waves ${isPlaying ? 'playing' : ''}`}>
                  {[...Array(5)].map((_, i) => (
                    <motion.div 
                      key={i} 
                      className="wave"
                      animate={isPlaying ? {
                        scaleY: [1, 1.5 + Math.random(), 1],
                        transition: {
                          duration: 0.5,
                          repeat: Infinity,
                          delay: i * 0.1
                        }
                      } : { scaleY: 1 }}
                    />
                  ))}
                </div>
              </div>

              <div className="audio-controls">
                {!audioUrl ? (
                  <motion.button
                    className={`btn btn-primary btn-large ${isGenerating ? 'loading' : ''}`}
                    onClick={generateAudio}
                    disabled={isGenerating}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isGenerating ? (
                      <>
                        <Loader className="spin" size={20} />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Mic size={20} />
                        <span>Generate Audio</span>
                      </>
                    )}
                  </motion.button>
                ) : (
                  <div className="playback-controls">
                    <motion.button
                      className={`btn ${isPlaying ? 'btn-danger' : 'btn-primary'}`}
                      onClick={isPlaying ? stopAudio : generateAudio}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    </motion.button>
                    <motion.button
                      className="btn btn-secondary"
                      onClick={() => {
                        stopAudio();
                        generateAudio();
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <RotateCcw size={20} />
                    </motion.button>
                    <motion.button
                      className="btn btn-secondary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Download size={20} />
                    </motion.button>
                  </div>
                )}
              </div>

              <AnimatePresence>
                {audioUrl && (
                  <motion.div 
                    className="audio-status"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <CheckCircle2 size={16} />
                    <span>Audio instructions ready</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* QR Code Section */}
          <motion.div className="section qr-section" variants={itemVariants}>
            <div className="section-header">
              <div className="section-icon">
                <QrCode size={20} />
              </div>
              <div>
                <h2>QR Code Sticker</h2>
                <p>Printable QR with all medicine details</p>
              </div>
            </div>

            <div className="section-content">
              <div className="qr-display">
                <motion.div 
                  className="qr-code-wrapper" 
                  ref={qrRef}
                  whileHover={{ scale: 1.02 }}
                >
                  {isSaving || !prescriptionUrl ? (
                    <div className="qr-loading">
                      <Loader className="spin" size={32} />
                      <p>Generating QR Code...</p>
                    </div>
                  ) : (
                    <QRCodeSVG
                      value={prescriptionUrl}
                      size={180}
                      level="M"
                      includeMargin={true}
                    />
                  )}
                  <div className="qr-overlay">
                    <Pill size={24} />
                  </div>
                </motion.div>
              </div>

              <div className="qr-steps">
                <div className="step">
                  <div className="step-icon">
                    <Download size={16} />
                  </div>
                  <span>Download QR</span>
                </div>
                <div className="step">
                  <div className="step-icon">
                    <Printer size={16} />
                  </div>
                  <span>Print sticker</span>
                </div>
                <div className="step">
                  <div className="step-icon">
                    <StickyNote size={16} />
                  </div>
                  <span>Attach to bottle</span>
                </div>
                <div className="step">
                  <div className="step-icon">
                    <Smartphone size={16} />
                  </div>
                  <span>Scan anytime</span>
                </div>
              </div>

              <motion.button 
                className="btn btn-secondary btn-full"
                onClick={downloadQRCode}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Download size={18} />
                <span>Download QR Code</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Medicine Checklist */}
          <motion.div className="section checklist-section" variants={itemVariants}>
            <div className="section-header">
              <div className="section-icon">
                <Pill size={20} />
              </div>
              <div>
                <h2>Medicine Checklist</h2>
                <p>Track your daily medicines</p>
              </div>
            </div>

            <div className="section-content">
              <div className="progress-bar">
                <motion.div 
                  className="progress-fill"
                  initial={{ width: 0 }}
                  animate={{ width: `${(checkedMeds.size / analysis.medicines.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="progress-text">
                {checkedMeds.size} of {analysis.medicines.length} medicines taken
              </p>

              <div className="checklist">
                {analysis.medicines.map((medicine, idx) => (
                  <motion.div
                    key={idx}
                    className={`checklist-item ${checkedMeds.has(idx) ? 'checked' : ''}`}
                    onClick={() => toggleMedicine(idx)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className={`checkbox ${checkedMeds.has(idx) ? 'checked' : ''}`}>
                      <AnimatePresence>
                        {checkedMeds.has(idx) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                          >
                            <CheckCircle2 size={20} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="item-content">
                      <strong>{medicine.name}</strong>
                      <span className="medicine-meta-line">
                        <span>{medicine.dosage}</span>
                        <span>{medicine.frequency}</span>
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          className="page-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <motion.button
            className="btn btn-primary"
            onClick={() => navigate('/cabinet', { state: { analysis } })}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Save size={20} />
            <span>Save to Cabinet</span>
          </motion.button>
          <motion.button
            className="btn btn-secondary"
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Home size={20} />
            <span>Back to Home</span>
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

